'use client';

import { c as _c } from "react/compiler-runtime";
import type { FactiiiRequirement, FactiiiTag, PinnedFactiii } from '@shared/all/domains/factiiis';
import { type Post } from '@shared/all/domains/posts';
import { calculateEditTronCostBreakdown, convertAwardToFactiiiTag, convertMyFactiiiTagsDataToFactiiiTag, convertPinnedFactiiiToFactiiiTag } from '@shared/utils/helpers/formatNumbers';
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useFactiiiHandlers } from '@shared/utils/hooks/useFactiiiHandlers';
import usePinnedFactiiis from '@shared/utils/hooks/usePinnedFactiiis';
import usePostsShared from '@shared/utils/hooks/usePosts';
import useUser from '@shared/utils/hooks/useUser';
import { type QueryKey, useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useInstanceSpaceSlug } from '../../hooks/useInstanceSpaceSlug';
import { useTronAffordability } from '../../hooks/useTronAffordability';
import { client } from '../../utils/trpc';
import { Camera } from '../common/Camera';
import Error from '../common/Error';
import { BotFactiii } from './FactiiiRenderers/BotFactiii';
import { SourceFactiii } from './FactiiiRenderers/SourceFactiii';
import { FactiiiSearch } from './FactiiiSearch';
import { FactiiiSearchResult } from './FactiiiSearchResult';
import { type SelectedAward, SelectedFactiiis } from './SelectedFactiiis';
type Props = {
  requirementsMet: {
    tag: FactiiiTag;
    results: {
      completed: boolean;
      description: string;
      requirement: FactiiiRequirement;
    }[];
  }[];
  queryKey: QueryKey;
  postId: string;
  pinnedFactiii?: PinnedFactiii;
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: Dispatch<SetStateAction<FactiiiTag[]>>;
  setNsfw: (nsfw: boolean) => void;
  setPolitics: (politics: boolean) => void;
  isPrivate?: boolean;
  isOpened: boolean;
  postFactiiisCount?: Post['postFactiiisCount'];
  postAuthorName?: string;
  // Awards "pretend to be factiiis": staged via the Awards pill and given on
  // Tag. Only wired up when tagging someone's post (postAuthorName set).
  selectedAwards?: SelectedAward[];
  setSelectedAwards?: Dispatch<SetStateAction<SelectedAward[]>>;
  // Receives the server-derived baseline (initialData) so the parent can show
  // a global Reset and gate Tag on whether the working set differs from it.
  setInitialData?: (data: FactiiiTag[]) => void;
  // When the parent renders the cost/balance summary itself (e.g. the modal
  // footer), suppress the in-body line so it doesn't eat vertical space.
  costInFooter?: boolean;
};
const getDefaultTimezone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offset = -new Date().getTimezoneOffset() / 60;
  return {
    value: timeZone,
    label: timeZone,
    offset
  };
};
export const NewPostFactiii = props => {
  const $ = _c(51);
  const {
    addSpaceTime,
    removeSpaceTime,
    addFactiii
  } = useFactiiiHandlers();
  const {
    currentUser
  } = useUser();
  const {
    pinnedFactiiis
  } = usePinnedFactiiis(client);
  const {
    fetchPrivateMyFactiiiTags
  } = usePostsShared(client);
  const awardsEnabled = !!props.postAuthorName && !!props.setSelectedAwards;
  const [searchFilter, setSearchFilter] = useState("all");
  const [cameraOpenId, setCameraOpenId] = useState(null);
  const instanceSpaceSlug = useInstanceSpaceSlug();
  const [spaceScope, setSpaceScope] = useState(instanceSpaceSlug);
  let t0;
  if ($[0] !== currentUser.id) {
    t0 = ["awards", currentUser.id];
    $[0] = currentUser.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== awardsEnabled || $[3] !== t0) {
    t1 = {
      queryKey: t0,
      queryFn: _temp,
      staleTime: Infinity,
      enabled: awardsEnabled
    };
    $[2] = awardsEnabled;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data: awardsData
  } = useQuery(t1);
  let t2;
  if ($[5] !== props.selectedAwards) {
    t2 = new Set((props.selectedAwards ?? []).map(_temp2));
    $[5] = props.selectedAwards;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const stagedAwardIds = t2;
  const givable = (awardsData?.awards ?? []).filter(_temp3);
  let t3;
  if ($[7] !== stagedAwardIds) {
    t3 = a_1 => !stagedAwardIds.has(a_1.factiiiId);
    $[7] = stagedAwardIds;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const availableAwards = givable.filter(t3);
  const awardOf = tag => givable.find(a_2 => a_2.factiiiId === tag.factiiiId);
  const awardCard = _temp4;
  let t4;
  if ($[9] !== props) {
    t4 = award_0 => {
      props.setSelectedAwards?.(prev => prev.some(a_3 => a_3.id === award_0.factiiiId) ? prev : [...prev, {
        id: award_0.factiiiId,
        title: award_0.tron.title,
        image: award_0.image,
        cost: award_0.tron.quantity ?? 0,
        description: award_0.description,
        anonymous: false,
        count: 1
      }]);
    };
    $[9] = props;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleAddAward = t4;
  const awardTag = tag_0 => {
    const award_1 = awardOf(tag_0);
    return award_1 && awardCard(award_1);
  };
  const handleAddFromSearch = tag_1 => {
    const award_2 = awardOf(tag_1);
    if (award_2) {
      handleAddAward(award_2);
    } else {
      handleAddFactiii(tag_1);
    }
  };
  let t5;
  if ($[11] !== props) {
    t5 = id => props.setSelectedAwards?.(prev_0 => prev_0.filter(a_4 => a_4.id !== id));
    $[11] = props;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleRemoveAward = t5;
  let t6;
  if ($[13] !== props) {
    t6 = (id_0, patch) => props.setSelectedAwards?.(prev_1 => prev_1.map(a_5 => a_5.id === id_0 ? {
      ...a_5,
      ...patch
    } : a_5));
    $[13] = props;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleUpdateAward = t6;
  const pills = [{
    key: "all",
    label: "All"
  }, {
    key: "sources",
    label: "Sources"
  }, {
    key: "bots",
    label: "Bots"
  }, ...(awardsEnabled ? [{
    key: "awards" as const,
    label: "Awards"
  }] : []), {
    key: "pinned",
    label: "Pinned"
  }];
  let t7;
  if ($[15] !== fetchPrivateMyFactiiiTags || $[16] !== props.isPrivate || $[17] !== props.pinnedFactiii || $[18] !== props.postId) {
    t7 = async () => {
      if (props.isPrivate) {
        return await fetchPrivateMyFactiiiTags(props.postId, props.pinnedFactiii?.id ? [props.pinnedFactiii.id] : []);
      } else {
        return unwrapFromResult(client.factiiis.myFactiiiTags.query({
          postId: props.postId,
          excludeFactiiiIds: props.pinnedFactiii?.id ? [props.pinnedFactiii.id] : []
        }));
      }
    };
    $[15] = fetchPrivateMyFactiiiTags;
    $[16] = props.isPrivate;
    $[17] = props.pinnedFactiii;
    $[18] = props.postId;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== props.queryKey || $[21] !== t7) {
    t8 = {
      queryKey: props.queryKey,
      queryFn: t7,
      refetchOnWindowFocus: false,
      gcTime: 0
    };
    $[20] = props.queryKey;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const {
    data: myFactiiiTagsData,
    isError: myFactiiiTagsError,
    isLoading: myFactiiiTagsLoading
  } = useQuery(t8);
  let t9;
  if ($[23] !== myFactiiiTagsData || $[24] !== props) {
    t9 = () => {
      if (!myFactiiiTagsData && !props.pinnedFactiii) {
        return;
      }
      const fetchedTags = (myFactiiiTagsData || []).map(_temp5);
      const pinned = props.pinnedFactiii ? [convertPinnedFactiiiToFactiiiTag(props.pinnedFactiii)] : [];
      const baseline = Array.from(new Map([...fetchedTags, ...pinned].map(_temp6)).values());
      props.setInitialData?.(baseline);
      props.setMyPostFactiiiCounts(prev_2 => {
        const byId = new Map(prev_2.map(_temp7));
        [...fetchedTags, ...pinned].forEach(t_1 => {
          if (!byId.has(t_1.factiiiId)) {
            byId.set(t_1.factiiiId, t_1);
          }
        });
        return Array.from(byId.values());
      });
    };
    $[23] = myFactiiiTagsData;
    $[24] = props;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== myFactiiiTagsData || $[27] !== props.pinnedFactiii) {
    t10 = [myFactiiiTagsData, props.pinnedFactiii];
    $[26] = myFactiiiTagsData;
    $[27] = props.pinnedFactiii;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  useEffect(t9, t10);
  let t11;
  if ($[29] !== props.isPrivate || $[30] !== props.myPostFactiiiCounts || $[31] !== props.postFactiiisCount || $[32] !== props.selectedAwards) {
    t11 = props.isPrivate ? {
      tagEditFee: 0,
      botCost: 0,
      awardCost: 0,
      total: 0
    } : calculateEditTronCostBreakdown(props.myPostFactiiiCounts, props.postFactiiisCount, (props.selectedAwards ?? []).map(_temp8));
    $[29] = props.isPrivate;
    $[30] = props.myPostFactiiiCounts;
    $[31] = props.postFactiiisCount;
    $[32] = props.selectedAwards;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const costBreakdown = t11;
  const tronCost = costBreakdown.total;
  const {
    insufficientTrons,
    poolCoversRemainder
  } = useTronAffordability(costBreakdown);
  let t12;
  if ($[34] !== props.myPostFactiiiCounts) {
    t12 = pf => !props.myPostFactiiiCounts.some(tag_3 => tag_3.factiiiId === pf.id);
    $[34] = props.myPostFactiiiCounts;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const filteredPinnedFactiiis = pinnedFactiiis.filter(t12);
  let t13;
  if ($[36] !== addSpaceTime || $[37] !== props.setMyPostFactiiiCounts) {
    t13 = (tag_4, entry) => addSpaceTime({
      tag: tag_4,
      ...entry,
      setMyPostFactiiiCounts: props.setMyPostFactiiiCounts
    });
    $[36] = addSpaceTime;
    $[37] = props.setMyPostFactiiiCounts;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const wrappedAddSpaceTime = t13;
  let t14;
  if ($[39] !== props.setMyPostFactiiiCounts || $[40] !== removeSpaceTime) {
    t14 = (tag_5, index) => removeSpaceTime({
      tag: tag_5,
      index,
      setMyPostFactiiiCounts: props.setMyPostFactiiiCounts
    });
    $[39] = props.setMyPostFactiiiCounts;
    $[40] = removeSpaceTime;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  const wrappedRemoveSpaceTime = t14;
  const handleAddFactiii = tag_6 => addFactiii({
    tag: tag_6,
    setMyPostFactiiiCounts: props.setMyPostFactiiiCounts,
    setPolitics: props.setPolitics,
    setNsfw: props.setNsfw,
    myPostFactiiiCounts: props.myPostFactiiiCounts
  });
  const t15 = "mt-2 flex flex-col";
  let t16;
  if ($[42] !== currentUser.tronsBalance || $[43] !== insufficientTrons || $[44] !== poolCoversRemainder || $[45] !== props.costInFooter || $[46] !== tronCost) {
    t16 = !props.costInFooter && tronCost > 0 && <div className={`flex items-center justify-between px-1 py-1.5 text-xs ${insufficientTrons ? "text-destructive dark:text-destructive" : "text-textSecondary"}`}><span>Cost estimate: <strong>{tronCost.toLocaleString()}</strong> trons</span><span>Balance:{" "}<strong>{currentUser.tronsBalance.toLocaleString()}</strong>{insufficientTrons && " \u2014 insufficient"}{poolCoversRemainder && <span className="text-primary">{" "}— community pool covers remainder</span>}</span></div>;
    $[42] = currentUser.tronsBalance;
    $[43] = insufficientTrons;
    $[44] = poolCoversRemainder;
    $[45] = props.costInFooter;
    $[46] = tronCost;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  const t17 = props.isOpened && <><SelectedFactiiis myPostFactiiiCounts={props.myPostFactiiiCounts} setMyPostFactiiiCounts={props.setMyPostFactiiiCounts} requirementsMet={props.requirementsMet} myFactiiiTagsData={myFactiiiTagsData} setCameraOpenId={setCameraOpenId} addSpaceTime={wrappedAddSpaceTime} removeSpaceTime={wrappedRemoveSpaceTime} defaultTimezone={getDefaultTimezone()} queryKey={props.queryKey} isPrivatePost={props.isPrivate} selectedAwards={props.selectedAwards} onRemoveAward={handleRemoveAward} onUpdateAward={handleUpdateAward} /><div className="overflow-x-hidden p-1"><div className={`mb-2 flex gap-2 ${!props.postAuthorName ? "mt-2" : ""}`}>{pills.map(pill => <button key={pill.key} className={`rounded-full px-3 py-1 text-xs font-emphasis transition-colors ${searchFilter === pill.key ? "bg-primary text-white" : "bg-background-secondary text-textSecondary hover:bg-background-light"}`} onClick={() => setSearchFilter(pill.key)}>{pill.label}</button>)}{spaceScope && searchFilter === "all" && <button className="ml-auto flex items-center gap-1 rounded-full bg-primary/15 px-3 py-1 text-xs font-emphasis text-primary transition-colors hover:bg-primary/25" onClick={() => setSpaceScope(undefined)} title="Search all of Factiii instead">s/{spaceScope}<X size={12} /></button>}</div>{searchFilter === "all" && <FactiiiSearch myPostFactiiiCounts={props.myPostFactiiiCounts} handleClick={handleAddFromSearch} awardTag={awardTag} hiddenIds={stagedAwardIds} spaceSlug={spaceScope} />}{searchFilter === "sources" && <SourceFactiii myPostFactiiiCounts={props.myPostFactiiiCounts} setMyPostFactiiiCounts={props.setMyPostFactiiiCounts} handleClick={handleAddFactiii} />}{searchFilter === "bots" && <BotFactiii myPostFactiiiCounts={props.myPostFactiiiCounts} handleClick={handleAddFactiii} />}{searchFilter === "awards" && <div className="space-y-2"><p className="px-1 text-xs text-textSecondary">Add an award, then set how many and Public/Private on it above.</p>{availableAwards.length === 0 ? <p className="py-4 text-center text-sm text-textSecondary">{givable.length === 0 ? "No awards available yet. Check back later!" : "All awards added."}</p> : availableAwards.map(award_3 => <FactiiiSearchResult key={award_3.factiiiId} tag={awardCard(award_3)} handleClick={() => handleAddAward(award_3)} />)}</div>}{searchFilter === "pinned" && <div className="space-y-3">{filteredPinnedFactiiis.length === 0 ? <p className="py-4 text-center text-sm text-textSecondary">{pinnedFactiiis.length === 0 ? "No pinned factiiis yet. Pin some factiiis to quickly access them here." : "No pinned factiiis match your search."}</p> : filteredPinnedFactiiis.map(pinnedFactiii => {
          const factiiiTag = convertPinnedFactiiiToFactiiiTag(pinnedFactiii);
          return <FactiiiSearchResult key={pinnedFactiii.id} tag={factiiiTag} handleClick={() => handleAddFactiii(factiiiTag)} />;
        })}</div>}</div><Error loading={myFactiiiTagsLoading} error={myFactiiiTagsError} />{cameraOpenId && <Camera closeHandler={() => setCameraOpenId(null)} setNewPhotoFcn={photo => {
      const cameraTag = props.myPostFactiiiCounts.find(tag_7 => tag_7.factiiiId === cameraOpenId);
      if (!cameraTag) {
        return toast("No Camera Tag Found", "error");
      }
      const updatedTag = {
        ...cameraTag,
        postFactiiis: [{
          ...cameraTag.postFactiiis[0],
          photo
        }, ...cameraTag.postFactiiis.slice(1)]
      };
      const newPostFactiiiCounts = props.myPostFactiiiCounts.map(tag_8 => tag_8.id === cameraTag.id ? updatedTag : tag_8);
      props.setMyPostFactiiiCounts(newPostFactiiiCounts);
      setCameraOpenId(null);
    }} />}</>;
  let t18;
  if ($[48] !== t16 || $[49] !== t17) {
    t18 = <div className={t15}>{t16}{t17}</div>;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  return t18;
};
function _temp() {
  return unwrapFromResult(client.awards.list.query());
}
function _temp2(a) {
  return a.id;
}
function _temp3(a_0) {
  return a_0.available;
}
function _temp4(award) {
  return convertAwardToFactiiiTag({
    id: award.factiiiId,
    title: award.tron.title,
    image: award.image,
    description: award.description,
    cost: award.tron.quantity ?? 0
  });
}
function _temp5(tag_2) {
  return convertMyFactiiiTagsDataToFactiiiTag(tag_2);
}
function _temp6(t) {
  return [t.factiiiId, t];
}
function _temp7(t_0) {
  return [t_0.factiiiId, t_0];
}
function _temp8(a_6) {
  return {
    cost: a_6.cost,
    count: a_6.count
  };
}